<template>
  <div ref="gridRef" class="grid" :class="[variantClass, noMarginClass]">
    <slot />
  </div>
</template>

<script setup lang="ts">
type GridVariant = 'default' | 'detail';

const props = defineProps({
  variant: {
    type: String as () => GridVariant,
    default: 'default',
  },
});

const componentName = 'grid';
const variantClass = `${componentName}--${props.variant}`;

const gridRef = ref<HTMLElement>();
const noMarginClass = ref();
let observer: MutationObserver | null = null;

onMounted(() => {
  if (!import.meta.client) return;

  // Create a MutationObserver to watch for changes in the child elements of the grid
  observer = new MutationObserver(() => {
    const hasChildren = gridRef.value?.childElementCount && gridRef.value?.childElementCount > 0;
    noMarginClass.value = !hasChildren ? 'no--margin' : '';
  });

  // Observe the gridRef for child list changes
  if (gridRef.value) {
    observer.observe(gridRef.value, { childList: true });

    // Execute once to set the initial state
    const hasChildren = gridRef.value.childElementCount > 0;
    noMarginClass.value = !hasChildren ? 'no--margin' : '';
  }
});

onBeforeUnmount(() => {
  // Disconnect the observer when the component is unmounted
  observer?.disconnect();
});
</script>

<style lang="postcss" scoped>
/* Grid base */
.grid {
  --grid-column-small: calc(var(--base-spacing) * 4.5);

  --grid-margin-bottom-default-small: calc(var(--base-spacing) * 5);
  --grid-margin-top-default-medium: calc(var(--base-spacing) * 6);
  --grid-margin-bottom-default-medium: calc(var(--base-spacing) * 6);

  --grid-margin-top-default-large: calc(var(--base-spacing) * 8);
  --grid-margin-bottom-default-large: calc(var(--base-spacing) * 8);

  display: grid;
  margin-bottom: var(--grid-margin-bottom-default-small);

  @media (--sm-viewport) {
    grid-template-columns: var(--grid-column-small) minmax(0, 1fr) var(--grid-column-small);
    > * {
      grid-column: 2;
    }
  }

  @media (--md-viewport) {
    margin-top: var(--grid-margin-top-default-medium);
    margin-bottom: var(--grid-margin-bottom-default-medium);
    > * {
      grid-column: 2;
    }
  }

  @media (--lg-viewport) {
    margin-top: var(--grid-margin-top-default-large);
    margin-bottom: var(--grid-margin-bottom-default-large);
  }

  &.no--margin {
    margin-top: 0;
    margin-bottom: 0;
  }
}

/* Grid for the Default layout */
.grid--default {
  /* Default Grid Variables */
  --grid-gap-default-small: calc(var(--base-spacing) * 8);
  --grid-gap-default-medium: calc(var(--base-spacing) * 8);
  --grid-gap-default-large: calc(var(--base-spacing) * 12);
  --grid-gap-default-extra-large: calc(var(--base-spacing) * 14);

  --grid-column-default-medium: calc(var(--base-spacing) * 4.5);
  --grid-column-default-large: calc(var(--base-spacing) * 10);

  --grid-margin-top-default-small: calc(var(--base-spacing) * 4);

  margin-top: var(--grid-margin-top-default-small);
  /* No margin-top on article pages, only here */

  :deep(.is--full-width) {
    grid-column: 1 / 4;
  }

  @media (--sm-viewport) {
    row-gap: var(--grid-gap-default-small);

    :deep(.is--full-width-small) {
      grid-column: 1 / 4;
    }
  }
  @media (--md-viewport) {
    row-gap: var(--grid-gap-default-medium);
    grid-template-columns:
      var(--grid-column-default-medium)
      minmax(0, 1fr)
      var(--grid-column-default-medium);
  }
  @media (--lg-viewport) {
    row-gap: var(--grid-gap-default-large);
    grid-template-columns:
      minmax(var(--grid-column-default-large), 1fr)
      minmax(0, var(--global-site-width))
      minmax(var(--grid-column-default-large), 1fr);
  }
  @media (--xl-viewport) {
    row-gap: var(--grid-gap-default-extra-large);
  }
}

/* Grid for the Detail layout */
.grid--detail {
  /* Article Grid Variables */
  --grid-gap-detail-small: calc(var(--base-spacing) * 4.5);
  --grid-gap-detail-medium: calc(var(--base-spacing) * 6);
  --grid-gap-detail-large: calc(var(--base-spacing) * 8);

  --grid-column-detail-medium-outer: calc(var(--base-spacing) * 6);
  --grid-column-detail-medium-inner: calc(var(--base-spacing) * 14);
  --grid-column-detail-large-outer: calc(var(--base-spacing) * 10);
  --grid-column-detail-large-between-max: calc(var(--base-spacing) * 28);
  --grid-column-detail-large-between-min: calc(var(--base-spacing) * 12);

  --grid-column-detail-large-inner-min: 32px;
  --grid-column-detail-large-inner-max: 96px;

  --grid-has-padding-small: 24px;
  --grid-has-padding-medium: 48px;

  @media (--sm-viewport) {
    row-gap: var(--grid-gap-detail-small);

    > * {
      grid-column: 2;
    }

    :deep(.is--full-detail-width-small) {
      grid-column: 1 / 4;
    }
  }

  @media (--md-viewport) {
    grid-template-columns:
      var(--grid-column-detail-medium-outer)
      var(--grid-column-detail-medium-inner)
      minmax(0, 1fr)
      var(--grid-column-detail-medium-inner)
      var(--grid-column-detail-medium-outer);

    row-gap: var(--grid-gap-detail-medium);

    > * {
      grid-column: 3;
    }

    :deep(.has--padding) {
      padding-left: var(--grid-has-padding-small);
      padding-right: var(--grid-has-padding-small);
    }

    :deep(.is--full-detail-width-medium) {
      grid-column: 2 / 5;
    }
  }

  @media (--lg-viewport) {
    grid-template-columns:
      minmax(var(--grid-column-detail-large-outer), 1fr)
      minmax(var(--grid-column-detail-large-inner-min), var(--grid-column-detail-large-inner-max))
      minmax(
        var(--grid-column-detail-large-between-min),
        var(--grid-column-detail-large-between-max)
      )
      var(--global-content-width)
      minmax(
        var(--grid-column-detail-large-between-min),
        var(--grid-column-detail-large-between-max)
      )
      minmax(var(--grid-column-detail-large-inner-min), var(--grid-column-detail-large-inner-max))
      minmax(var(--grid-column-detail-large-outer), 1fr);

    row-gap: var(--grid-gap-detail-large);

    > * {
      grid-column: 4 / 5;
    }

    :deep(.has--padding) {
      padding-left: var(--grid-has-padding-medium);
      padding-right: var(--grid-has-padding-medium);
    }

    :deep(.is--full-detail-width-medium) {
      grid-column: 4 / 5;
    }

    :deep(.is--full-detail-width-large) {
      grid-column: 3 / 6;
    }

    :deep(.is--full-page-width) {
      grid-column: 2 / 7;
    }
  }
}
</style>
